import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { pathKey } from '~/apps/ninesegsAnalyzer/utils/standardAnalysis'
import {
  type Brand,
  useSurveyQueryActiveBrandGroup,
  useSurveyQueryData,
  useSurveyQuerySurveyFilterTextGroups,
} from '~/hooks/useSurveyQuery'
import {
  assertExists,
  dataDisplayTypeLabelI18nKeyMap,
  type FigureInfo,
  FigureInfoHelpers,
  segmentDisplayTypeLabelI18nKeyMap,
} from '~/utils'

import {
  customerAttributeIdsAtom,
  dataDisplayTypeAtom,
  questionIdsAtom,
  segmentDisplayTypeAtom,
} from '../atoms'

export const useFigureInfo = (brand: Brand): FigureInfo => {
  const { t } = useTranslation()
  const survey = useSurveyQueryData()
  const brandGroup = useSurveyQueryActiveBrandGroup()

  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const segmentDisplayType = useAtomValue(segmentDisplayTypeAtom)
  const questionIds = useAtomValue(questionIdsAtom)
  const customerAttributeIds = useAtomValue(customerAttributeIdsAtom)

  const surveyFilterTextGroups = useSurveyQuerySurveyFilterTextGroups()

  return React.useMemo(() => {
    assertExists(survey, t('assertion_failed'))
    const brandAccountId = survey.brandAccountId

    return {
      brandAccountId,
      brandGroup: brandGroup?.name,
      dataDisplayType: dataDisplayTypeLabelI18nKeyMap[dataDisplayType],
      pathKey,
      brand: brand.name,
      questions: FigureInfoHelpers.toQuestionTexts(
        questionIds,
        survey.questions,
      ),
      customerAttributes: FigureInfoHelpers.toCustomerAttributeTexts(
        customerAttributeIds,
        survey.customerAttributeGroups,
        t,
      ),
      segmentDisplayType: segmentDisplayTypeLabelI18nKeyMap[segmentDisplayType],
      survey: survey.title,
      surveyFilter: surveyFilterTextGroups,
    }
  }, [
    brand.name,
    brandGroup?.name,
    customerAttributeIds,
    dataDisplayType,
    questionIds,
    segmentDisplayType,
    survey,
    surveyFilterTextGroups,
    t,
  ])
}
